import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
    Download,
    FileText,
    X,
    User,
    Building2,
    Home,
    CreditCard,
    DollarSign,
    PieChart,
    AlertTriangle,
    CheckCircle2,
    FileSearch,
    ListChecks,
    Search,
    Menu,
    Lightbulb,
    AlertOctagon,
    Lock,
    ShieldCheck,
    Siren,
    ClipboardList,
    Scale,
    BarChart,
    Check,
    Cloud
} from "lucide-react";
import html2pdf from "html2pdf.js";
import auditData from "../mocks/underwriting_audits_mock.json";
import { LogoBase64 } from "./LogoAsset";
import forensicData from "../mocks/underwriting_results_gemini_forensic.json";

// Process Audit Data
const report2_Audit = (() => {
    const review = auditData.underwriting_review || {};
    const dti = review.debt_to_income_analysis || {};
    const bestCase = dti.best_case_scenario || {};
    const conservativeCase = dti.conservative_scenario || {};

    return {
        metadata: {
            institution: review.metadata?.institution || "N/A",
            risk: review.metadata?.risk_rating || "N/A",
            status: review.metadata?.review_status || "PENDING"
        },
        executive_summary: {
            assessment: review.executive_summary?.overall_assessment || ""
        },
        critical_findings: (review.critical_findings || []).map((finding) => ({
            severity: finding.severity || "INFO",
            id: finding.finding_id || finding.category || "Finding",
            title: finding.title || finding.category || "Finding",
            desc: finding.description || finding.recommendation || "",
            impact: finding.impact || ""
        })),
        data_quality: (review.data_quality_issues || []).map((issue) => ({
            id: issue.issue_id || issue.category || "Data Quality",
            title: issue.title || issue.category || "Data Quality",
            details: issue.recommendation || issue.concern || ""
        })),
        compliance_issues: (review.compliance_best_practice_issues || []).map((issue) => ({
            id: issue.issue_id || issue.category || "Compliance",
            title: issue.title || issue.category || "Compliance",
            desc: issue.recommendation || ""
        })),
        dti_analysis: {
            optimistic: {
                name: bestCase.scenario_name || "Optimistic",
                dti: bestCase.dti_ratio || 0,
                income: bestCase.monthly_income?.total_qualifying_income || 0
            },
            conservative: {
                name: conservativeCase.scenario_name || "Conservative",
                dti: conservativeCase.dti_ratio || 0,
                income: conservativeCase.monthly_income?.total_qualifying_income || 0
            }
        },
        conditions_precedent: {
            tier1:
                review.conditions_precedent?.tier_1_critical?.conditions?.map(
                    (condition) => condition.requirement || condition.condition_id || ""
                ) || [],
            tier2:
                review.conditions_precedent?.tier_2_important?.conditions?.map(
                    (condition) => condition.requirement || condition.condition_id || ""
                ) || []
        },
        final_recommendation: {
            decision: review.final_recommendation?.decision || "PENDING",
            rationale:
                review.final_recommendation?.rationale ||
                review.debt_to_income_analysis?.conclusion ||
                ""
        },
        certification:
            review.underwriter_certification?.statement ||
            review.underwriter_certification?.review_completeness ||
            ""
    };
})();

// Process Forensic Data
const report3_Forensic = (() => {
    const applicants = forensicData.borrowers || forensicData.applicants || [];
    const businesses = forensicData.businesses || [];
    const properties = forensicData.properties || [];
    const liabilities = forensicData.liabilities_monthly_estimate || forensicData.liabilities_summary || {};
    const incomeSummary = forensicData.income_summary_for_uw || {};
    const incomeAnalysis = forensicData.income_analysis || {};

    const bankBusinessAccounts = businesses.map((biz) => {
        const summaries = [];
        const yr = biz.banking?.year_2024_summary;
        if (yr) {
            summaries.push({
                year: 2024,
                total_deposits: yr.total_deposits || 0,
                total_withdrawals: yr.total_withdrawals || 0,
                net_cash_flow: yr.net_cash_flow || 0,
                ending_balance: yr.end_balance || yr.ending_balance || 0
            });
        }
        const selected = Array.isArray(biz.banking?.selected_months_2025) ? biz.banking.selected_months_2025 : [];
        const ending = selected.length ? selected[selected.length - 1].end_balance : undefined;
        if (summaries.length && ending !== undefined) {
            summaries[0].ending_balance = ending;
        }
        return {
            institution: biz.banking?.institution || biz.name,
            account_type: biz.banking?.account_type || "Business Operating",
            account_last4: biz.banking?.account_no_masked || "----",
            annual_summaries: summaries,
            notes: biz.authority_verification_required || ""
        };
    });

    const personalAccounts = [];
    applicants.forEach((applicant) => {
        (applicant.personal_bank_accounts || []).forEach((acct) => {
            personalAccounts.push({
                institution: acct.institution,
                account_type: acct.account_type || "Checking",
                account_last4: acct.account_no_masked || "----",
                monthly_snapshots: (acct.periods || []).map((p) => ({
                    period: p.period,
                    deposits: p.deposits,
                    withdrawals: p.withdrawals,
                    ending_balance: p.end_balance
                })),
                notes: acct.notes || ""
            });
        });
    });

    const bankAnalysis = forensicData.bank_accounts_analysis || {};
    const bank = {
        business_accounts:
            (bankAnalysis.business_accounts && bankAnalysis.business_accounts.length
                ? bankAnalysis.business_accounts
                : bankBusinessAccounts) || [],
        personal_accounts:
            (bankAnalysis.personal_accounts && bankAnalysis.personal_accounts.length
                ? bankAnalysis.personal_accounts
                : personalAccounts) || []
    };

    const investments =
        forensicData.investment_assets ||
        (forensicData.reserves_estimate?.months_of_reserves || []).map((reserve) => ({
            account: reserve.assets_considered,
            owner: applicants[0]?.name || "Borrower",
            value_history: [],
            notes: `Counts toward reserves (${reserve.months_vs_estimated_PITI_stack} months PITI).`
        }));

    const risks =
        forensicData.risk_and_trends ||
        (forensicData.risk_observations || []).map((r, idx) => ({
            risk_id: `FR-${String(idx + 1).padStart(2, "0")}`,
            category: "Forensic Observation",
            description: r,
            severity: "Medium"
        }));

    const followUps = forensicData.required_follow_ups || forensicData.next_steps_conditions || [];

    const income = incomeAnalysis && Object.keys(incomeAnalysis).length
        ? {
            ...incomeAnalysis,
            qualifying_income_summary:
                incomeAnalysis.qualifying_income_summary ||
                (incomeSummary.base_employment_monthly
                    ? { base_employment_income_gross_monthly: incomeSummary.base_employment_monthly }
                    : {})
        }
        : {
            qualifying_income_summary: {
                base_employment_income_gross_monthly: incomeSummary.base_employment_monthly || 0,
                notes:
                    "Primary qualifying income from W-2. Rental cash flow adjusted for observed PITIA and estimated Regions obligation."
            }
        };

    const documents =
        forensicData.documents_referenced ||
        forensicData.references?.documents ||
        [];

    return {
        applicants,
        businesses,
        bank,
        investments,
        risks,
        followUps,
        income,
        liabilities,
        documents
    };
})();

/**
 * Enhanced Report Renderer
 * Supports Structured JSON Data from AI Models
 */
const DetailedUnderwritingReport = ({ isOpen, onClose, autoDownload = false, fileName = "Underwriting_Report.pdf", reportUrl = "/mocks/mock_underwriting_summary-gpt-5.json" }) => {
    const reportRef = useRef(null);
    const containerRef = useRef(null);
    const [sections, setSections] = useState([]);
    const [activeSection, setActiveSection] = useState(0);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeReportTab, setActiveReportTab] = useState('underwriting'); // 'underwriting' | 'audit' | 'forensic'
    const [compositeLogo, setCompositeLogo] = useState(null);

    // Create a composite logo with dark background for PDF rendering
    useEffect(() => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            // Set canvas size with padding
            const padding = 32;
            canvas.width = img.width + (padding * 2);
            canvas.height = img.height + (padding * 2);

            // Draw dark background (slate-900: rgb(15, 23, 42))
            ctx.fillStyle = 'rgb(15, 23, 42)';
            ctx.roundRect(0, 0, canvas.width, canvas.height, 80);
            ctx.fill();

            // Draw logo on top
            ctx.drawImage(img, padding, padding);

            // Convert to base64
            setCompositeLogo(canvas.toDataURL('image/png'));
        };
        img.src = LogoBase64;
    }, []);

    // Fetch and process report data
    useEffect(() => {
        if (isOpen) {
            setLoading(true);
            setError(null);
            fetch(reportUrl)
                .then(res => {
                    if (!res.ok) throw new Error("Failed to fetch report data");
                    return res.json();
                })
                .then(data => {
                    const processed = processReportData(data);
                    setSections(processed);
                    setLoading(false);
                })
                .catch(err => {
                    console.error("Report Load Error:", err);
                    setError(err.message);
                    setLoading(false);
                });
        }
    }, [isOpen, reportUrl]);

    // Transform raw JSON into Renderable Blocks
    const processReportData = (data) => {
        // Handle new object-based report format (keys like "0_meta_data", "1_executive_summary", etc.)
        if (data && typeof data === 'object' && !Array.isArray(data)) {
            const sections = [];

            // Sort keys to ensure correct order (0_, 1_, 2_, etc.)
            const sortedKeys = Object.keys(data).sort((a, b) => {
                const numA = parseInt(a.split('_')[0]) || 999;
                const numB = parseInt(b.split('_')[0]) || 999;
                return numA - numB;
            });

            for (const key of sortedKeys) {
                const item = data[key];

                // Skip metadata and full markdown report
                if (key === '0_meta_data' || key === '15_markdown_full_report') continue;

                // Handle references array
                if (key === '14_references' && Array.isArray(item)) {
                    sections.push({
                        title: 'References',
                        sectionKey: key,
                        isRisk: false,
                        blocks: [{ type: 'references', items: item }]
                    });
                    continue;
                }

                // Handle regular sections with section_id
                if (item && typeof item === 'object' && item.section_id) {
                    const sectionTitle = item.section_id;
                    const isRisk = sectionTitle.toLowerCase().includes('risk') ||
                                   sectionTitle.toLowerCase().includes('audit') ||
                                   (item.warnings && item.warnings.length > 0);

                    const blocks = [
                        // Summary
                        { type: 'paragraph', content: item.summary },
                        // Key Insights
                        ...(item.key_insight && item.key_insight.length > 0 ? [{ type: 'insights', items: item.key_insight }] : []),
                        // Warnings
                        ...(item.warnings && item.warnings.length > 0 ? [{ type: 'warnings', items: item.warnings }] : []),
                    ];

                    // Handle substructure for special sections
                    if (item.substructure) {
                        blocks.push({ type: 'substructure', data: item.substructure, sectionKey: key });
                    }

                    // Handle URLA Analysis specific fields
                    if (key === '12_urla_analysis' && item.substructure) {
                        blocks.push({ type: 'urla_analysis', data: item.substructure });
                    }

                    // Handle Credit Report Analysis specific fields
                    if (key === '13_credit_report_analysis' && item.substructure) {
                        blocks.push({ type: 'credit_report_analysis', data: item.substructure });
                    }

                    // Handle markdown content if present
                    if (item.markdown) {
                        blocks.push({ type: 'markdown', content: item.markdown });
                    }

                    sections.push({
                        title: sectionTitle,
                        sectionKey: key,
                        isRisk,
                        blocks
                    });
                }
            }

            return sections;
        }

        // Handle legacy array format
        if (!Array.isArray(data)) return [];

        return data
            .filter(item => item.section_id !== "Markdown")
            .map(item => ({
                title: item.section_id,
                isRisk: item.section_id.toLowerCase().includes('risk') || (item.warnings && item.warnings.length > 0),
                blocks: [
                    { type: 'paragraph', content: item.summary },
                    ...(item.key_insight && item.key_insight.length > 0 ? [{ type: 'insights', items: item.key_insight }] : []),
                    ...(item.warnings && item.warnings.length > 0 ? [{ type: 'warnings', items: item.warnings }] : [])
                ]
            }));
    };

    // Filter sections based on active tab
    // Filter sections based on active tab
    const getVisibleSections = () => {
        // Underwriting report depends on fetched data
        if (activeReportTab === 'underwriting') {
            if (loading || !sections.length) return [];
            return sections;
        }

        // Audit & Forensic are from static mocks/imports in this version
        if (activeReportTab === 'audit') {
            return [
                { title: 'Executive Summary', isRisk: false },
                { title: 'Critical Findings', isRisk: true },
                { title: 'Data & Compliance', isRisk: true },
                { title: 'DTI Stress Test', isRisk: false },
                { title: 'Conditions Precedent', isRisk: false },
                { title: 'Final Recommendation', isRisk: false },
            ];
        }

        if (activeReportTab === 'forensic') {
            return [
                { title: 'Applicants & Business', isRisk: false },
                { title: 'Banking Analysis', isRisk: false },
                { title: 'Investment & Income', isRisk: false },
                { title: 'Forensic Risks', isRisk: true },
                { title: 'Documents Referenced', isRisk: false },
            ];
        }

        return [];
    };

    const handleDownload = () => {
        if (!reportRef.current) return;
        const options = {
            margin: 0.3,
            filename: activeReportTab === 'underwriting' ? fileName : `${activeReportTab}_report.pdf`,
            image: { type: "jpeg", quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true },
            jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
            pagebreak: { mode: ["css", "legacy"], avoid: ".pdf-avoid-break" }
        };
        html2pdf().from(reportRef.current).set(options).save();
    };

    const scrollToSection = (index) => {
        setActiveSection(index);
        setIsMobileMenuOpen(false);
        const element = document.getElementById(`report-section-${index}`);
        if (element && containerRef.current) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    // Helper: Get Icon based on title keywords
    const getSectionIcon = (title) => {
        const t = title.toLowerCase();
        if (t.includes("borrower")) return <User className="w-5 h-5" />;
        if (t.includes("business")) return <Building2 className="w-5 h-5" />;
        if (t.includes("estate") || t.includes("property")) return <Home className="w-5 h-5" />;
        if (t.includes("liabilities") || t.includes("debt")) return <CreditCard className="w-5 h-5" />;
        if (t.includes("income")) return <DollarSign className="w-5 h-5" />;
        if (t.includes("reserves") || t.includes("dti") || t.includes("asset")) return <PieChart className="w-5 h-5" />;
        if (t.includes("risk") || t.includes("findings")) return <AlertTriangle className="w-5 h-5" />;
        if (t.includes("step") || t.includes("condition")) return <ListChecks className="w-5 h-5" />;
        if (t.includes("reference")) return <FileSearch className="w-5 h-5" />;
        if (t.includes("urla") || t.includes("loan application")) return <ClipboardList className="w-5 h-5" />;
        if (t.includes("credit report") || t.includes("credit analysis")) return <ShieldCheck className="w-5 h-5" />;
        if (t.includes("forensic")) return <Search className="w-5 h-5" />;
        if (t.includes("audit")) return <FileSearch className="w-5 h-5" />;
        if (t.includes("executive")) return <FileText className="w-5 h-5" />;
        return <FileText className="w-5 h-5" />;
    };

    // Generic Highlighter
    const highlightKeywords = (text) => {
        if (!text) return '';
        // Only support basic markdown-style bold text for consistency
        let formatted = text
            .replace(/\*\*([^\*]+)\*\*/g, '<strong class="font-semibold text-slate-900">$1</strong>')
            .replace(/`([^`]+)`/g, '<code class="bg-slate-100 px-1 py-0.5 rounded text-xs font-mono text-slate-600 border border-slate-200">$1</code>');

        return formatted;
    };

    // Block Renderer
    const renderBlock = (block, idx) => {
        switch (block.type) {
            case 'paragraph':
                return (
                    <p key={idx} className="text-sm text-slate-700 mb-4 leading-relaxed whitespace-pre-line pdf-avoid-break">
                        <span dangerouslySetInnerHTML={{ __html: highlightKeywords(block.content) }} />
                    </p>
                );

            case 'insights':
                return (
                    <div key={idx} className="mb-6 bg-emerald-50/50 rounded-lg border border-emerald-100 p-4 pdf-avoid-break">
                        <div className="flex items-center gap-2 mb-3 text-emerald-800 font-semibold text-sm">
                            <Lightbulb className="w-4 h-4 text-emerald-600" />
                            Key Insights
                        </div>
                        <div className="space-y-2">
                            {block.items.map((item, i) => (
                                <div key={i} className="flex items-start gap-3">
                                    <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                                    <span className="text-sm text-slate-700" dangerouslySetInnerHTML={{ __html: highlightKeywords(item) }} />
                                </div>
                            ))}
                        </div>
                    </div>
                );

            case 'warnings':
                return (
                    <div key={idx} className="mb-4 bg-amber-50/50 rounded-lg border border-amber-100 p-4 pdf-avoid-break">
                        <div className="flex items-center gap-2 mb-3 text-amber-800 font-semibold text-sm">
                            <AlertOctagon className="w-4 h-4 text-amber-600" />
                            Warnings & Discrepancies
                        </div>
                        <div className="space-y-2">
                            {block.items.map((item, i) => (
                                <div key={i} className="flex items-start gap-3">
                                    <div className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 shrink-0"></div>
                                    <span className="text-sm text-slate-700" dangerouslySetInnerHTML={{ __html: highlightKeywords(item) }} />
                                </div>
                            ))}
                        </div>
                    </div>
                );

            case 'references':
                return (
                    <div key={idx} className="mb-6 bg-slate-50 rounded-lg border border-slate-200 p-4 pdf-avoid-break">
                        <div className="flex items-center gap-2 mb-3 text-slate-800 font-semibold text-sm">
                            <FileText className="w-4 h-4 text-slate-600" />
                            Documents Referenced ({block.items.length})
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                            {block.items.map((item, i) => (
                                <div key={i} className="flex items-center gap-2 text-xs text-slate-600 bg-white px-2 py-1.5 rounded border border-slate-100">
                                    <FileText className="w-3 h-3 text-slate-400 shrink-0" />
                                    <span className="truncate">{item}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                );

            case 'urla_analysis':
                return (
                    <div key={idx} className="space-y-4 pdf-avoid-break">
                        {/* Borrower Profile */}
                        {block.data.borrower_profile && (
                            <div className="bg-sky-50/50 rounded-lg border border-sky-100 p-4">
                                <h4 className="text-sm font-semibold text-sky-800 mb-3 flex items-center gap-2">
                                    <User className="w-4 h-4" /> Borrower Profile
                                </h4>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                                    <div><span className="text-slate-500">Name:</span> <span className="font-medium text-slate-800">{block.data.borrower_profile.full_name}</span></div>
                                    <div><span className="text-slate-500">SSN:</span> <span className="font-medium text-slate-800">{block.data.borrower_profile.ssn_masked}</span></div>
                                    <div><span className="text-slate-500">DOB:</span> <span className="font-medium text-slate-800">{block.data.borrower_profile.dob}</span></div>
                                    <div><span className="text-slate-500">Marital Status:</span> <span className="font-medium text-slate-800">{block.data.borrower_profile.marital_status}</span></div>
                                    <div><span className="text-slate-500">Dependents:</span> <span className="font-medium text-slate-800">{block.data.borrower_profile.dependents}</span></div>
                                    <div><span className="text-slate-500">Ownership:</span> <span className="font-medium text-slate-800">{block.data.borrower_profile.ownership_status}</span></div>
                                    <div className="col-span-2 md:col-span-3"><span className="text-slate-500">Address:</span> <span className="font-medium text-slate-800">{block.data.borrower_profile.current_address}</span></div>
                                </div>
                            </div>
                        )}

                        {/* Employment Stated */}
                        {block.data.employment_stated && (
                            <div className="bg-indigo-50/50 rounded-lg border border-indigo-100 p-4">
                                <h4 className="text-sm font-semibold text-indigo-800 mb-3 flex items-center gap-2">
                                    <Building2 className="w-4 h-4" /> Employment (Stated)
                                </h4>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                                    <div><span className="text-slate-500">Employer:</span> <span className="font-medium text-slate-800">{block.data.employment_stated.current_employer}</span></div>
                                    <div><span className="text-slate-500">Position:</span> <span className="font-medium text-slate-800">{block.data.employment_stated.position_title}</span></div>
                                    <div><span className="text-slate-500">Start Date:</span> <span className="font-medium text-slate-800">{block.data.employment_stated.start_date}</span></div>
                                    <div><span className="text-slate-500">Years in Profession:</span> <span className="font-medium text-slate-800">{block.data.employment_stated.years_in_profession}</span></div>
                                    <div><span className="text-slate-500">Self-Employed:</span> <span className="font-medium text-slate-800">{block.data.employment_stated.self_employed ? 'Yes' : 'No'}</span></div>
                                </div>
                            </div>
                        )}

                        {/* Income Stated */}
                        {block.data.income_stated && (
                            <div className="bg-emerald-50/50 rounded-lg border border-emerald-100 p-4">
                                <h4 className="text-sm font-semibold text-emerald-800 mb-3 flex items-center gap-2">
                                    <DollarSign className="w-4 h-4" /> Income (Stated)
                                </h4>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                    <div><span className="text-slate-500">Base:</span> <span className="font-medium text-slate-800">${block.data.income_stated.base_monthly?.toLocaleString()}/mo</span></div>
                                    <div><span className="text-slate-500">Bonus:</span> <span className="font-medium text-slate-800">${block.data.income_stated.bonus_monthly?.toLocaleString()}/mo</span></div>
                                    <div><span className="text-slate-500">Rental:</span> <span className="font-medium text-slate-800">${block.data.income_stated.rental_income_monthly?.toLocaleString()}/mo</span></div>
                                    <div><span className="text-slate-500">Self-Emp:</span> <span className="font-medium text-slate-800">${block.data.income_stated.self_employment_monthly?.toLocaleString()}/mo</span></div>
                                    <div className="col-span-2 md:col-span-4 pt-2 border-t border-emerald-200">
                                        <span className="text-slate-600 font-semibold">Total Stated:</span> <span className="font-bold text-emerald-700 text-lg">${block.data.income_stated.total_monthly_stated?.toLocaleString()}/mo</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Assets Stated */}
                        {block.data.assets_stated && block.data.assets_stated.length > 0 && (
                            <div className="bg-amber-50/50 rounded-lg border border-amber-100 p-4">
                                <h4 className="text-sm font-semibold text-amber-800 mb-3 flex items-center gap-2">
                                    <PieChart className="w-4 h-4" /> Assets (Stated)
                                </h4>
                                <div className="space-y-2">
                                    {block.data.assets_stated.map((asset, i) => (
                                        <div key={i} className="flex items-center justify-between text-sm bg-white p-2 rounded border border-amber-100">
                                            <div>
                                                <span className="font-medium text-slate-800">{asset.asset_type}</span>
                                                <span className="text-slate-500 ml-2">- {asset.institution} (***{asset.account_number_last4})</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="font-semibold text-amber-700">${asset.current_value?.toLocaleString()}</span>
                                                {asset.verified && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Liabilities Stated */}
                        {block.data.liabilities_stated && block.data.liabilities_stated.length > 0 && (
                            <div className="bg-red-50/50 rounded-lg border border-red-100 p-4">
                                <h4 className="text-sm font-semibold text-red-800 mb-3 flex items-center gap-2">
                                    <CreditCard className="w-4 h-4" /> Liabilities (Stated)
                                </h4>
                                <div className="space-y-2">
                                    {block.data.liabilities_stated.map((liability, i) => (
                                        <div key={i} className="flex items-center justify-between text-sm bg-white p-2 rounded border border-red-100">
                                            <div>
                                                <span className="font-medium text-slate-800">{liability.creditor}</span>
                                                <span className="text-slate-500 ml-2">- {liability.account_type}</span>
                                            </div>
                                            <div className="text-right">
                                                <span className="font-semibold text-red-700">${liability.balance?.toLocaleString()}</span>
                                                <span className="text-slate-500 ml-2">(${liability.monthly_payment?.toLocaleString()}/mo)</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Discrepancies Found */}
                        {block.data.discrepancies_found && block.data.discrepancies_found.length > 0 && (
                            <div className="bg-red-100/50 rounded-lg border border-red-200 p-4">
                                <h4 className="text-sm font-semibold text-red-800 mb-3 flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4" /> Discrepancies Found
                                </h4>
                                <div className="space-y-2">
                                    {block.data.discrepancies_found.map((item, i) => (
                                        <div key={i} className="flex items-start gap-2 text-sm text-red-700">
                                            <AlertOctagon className="w-4 h-4 shrink-0 mt-0.5" />
                                            <span>{item}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                );

            case 'credit_report_analysis':
                return (
                    <div key={idx} className="space-y-4 pdf-avoid-break">
                        {/* Credit Scores */}
                        {block.data.credit_scores && (
                            <div className="bg-indigo-50/50 rounded-lg border border-indigo-100 p-4">
                                <h4 className="text-sm font-semibold text-indigo-800 mb-3 flex items-center gap-2">
                                    <ShieldCheck className="w-4 h-4" /> Credit Scores
                                </h4>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                    <div className="text-center p-3 bg-white rounded-lg border border-indigo-100">
                                        <div className="text-xs text-slate-500 uppercase">Experian</div>
                                        <div className="text-2xl font-bold text-indigo-600">{block.data.credit_scores.experian_fico2}</div>
                                    </div>
                                    <div className="text-center p-3 bg-white rounded-lg border border-indigo-100">
                                        <div className="text-xs text-slate-500 uppercase">Equifax</div>
                                        <div className="text-2xl font-bold text-indigo-600">{block.data.credit_scores.equifax_fico5}</div>
                                    </div>
                                    <div className="text-center p-3 bg-white rounded-lg border border-indigo-100">
                                        <div className="text-xs text-slate-500 uppercase">TransUnion</div>
                                        <div className="text-2xl font-bold text-indigo-600">{block.data.credit_scores.transunion_fico4}</div>
                                    </div>
                                    <div className="text-center p-3 bg-indigo-100 rounded-lg border-2 border-indigo-300">
                                        <div className="text-xs text-indigo-600 uppercase font-semibold">Middle Score</div>
                                        <div className="text-3xl font-bold text-indigo-700">{block.data.credit_scores.middle_score}</div>
                                    </div>
                                </div>
                                {block.data.credit_scores.score_factors && (
                                    <div className="text-xs text-slate-600">
                                        <span className="font-semibold">Score Factors:</span> {block.data.credit_scores.score_factors.join(' | ')}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Trade Accounts */}
                        {block.data.trade_accounts && block.data.trade_accounts.length > 0 && (
                            <div className="bg-slate-50 rounded-lg border border-slate-200 p-4">
                                <h4 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
                                    <CreditCard className="w-4 h-4" /> Trade Accounts ({block.data.trade_accounts.length})
                                </h4>
                                <div className="space-y-2">
                                    {block.data.trade_accounts.map((acct, i) => (
                                        <div key={i} className={`p-3 rounded-lg border ${acct.late_30_count > 0 || acct.late_60_count > 0 ? 'bg-red-50 border-red-200' : 'bg-white border-slate-100'}`}>
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-semibold text-slate-800">{acct.creditor}</span>
                                                    <span className="text-xs px-2 py-0.5 bg-slate-100 rounded text-slate-600">{acct.account_type}</span>
                                                </div>
                                                <span className="text-xs text-slate-500">***{acct.account_number_last4}</span>
                                            </div>
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                                                <div><span className="text-slate-500">Balance:</span> <span className="font-medium">${acct.current_balance?.toLocaleString()}</span></div>
                                                <div><span className="text-slate-500">Limit:</span> <span className="font-medium">${acct.credit_limit?.toLocaleString() || 'N/A'}</span></div>
                                                <div><span className="text-slate-500">Payment:</span> <span className="font-medium">${acct.monthly_payment}/mo</span></div>
                                                <div><span className="text-slate-500">Opened:</span> <span className="font-medium">{acct.date_opened}</span></div>
                                            </div>
                                            {(acct.late_30_count > 0 || acct.late_60_count > 0 || acct.late_90_count > 0) && (
                                                <div className="mt-2 pt-2 border-t border-red-200 flex items-center gap-2">
                                                    <AlertTriangle className="w-3 h-3 text-red-500" />
                                                    <span className="text-xs text-red-600">
                                                        Late payments: {acct.late_30_count}x 30d, {acct.late_60_count}x 60d, {acct.late_90_count}x 90d
                                                    </span>
                                                </div>
                                            )}
                                            {acct.remarks && <div className="mt-1 text-xs text-slate-500 italic">{acct.remarks}</div>}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Derogatory Items */}
                        {block.data.derogatory_items && (
                            <div className="bg-amber-50/50 rounded-lg border border-amber-100 p-4">
                                <h4 className="text-sm font-semibold text-amber-800 mb-3 flex items-center gap-2">
                                    <AlertOctagon className="w-4 h-4" /> Derogatory Items
                                </h4>
                                {block.data.derogatory_items.collections && block.data.derogatory_items.collections.length > 0 && (
                                    <div className="mb-3">
                                        <div className="text-xs font-semibold text-amber-700 mb-2">Collections:</div>
                                        {block.data.derogatory_items.collections.map((col, i) => (
                                            <div key={i} className="flex items-center justify-between text-sm bg-white p-2 rounded border border-amber-100 mb-1">
                                                <span>{col.creditor}</span>
                                                <div className="flex items-center gap-2">
                                                    <span className="font-medium">${col.amount}</span>
                                                    <span className={`text-xs px-2 py-0.5 rounded ${col.status === 'Paid in Full' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{col.status}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {block.data.derogatory_items.late_payments_summary && (
                                    <div className="text-xs text-amber-700 bg-amber-100 p-2 rounded">{block.data.derogatory_items.late_payments_summary}</div>
                                )}
                            </div>
                        )}

                        {/* Debt Summary */}
                        {block.data.debt_summary && (
                            <div className="bg-sky-50/50 rounded-lg border border-sky-100 p-4">
                                <h4 className="text-sm font-semibold text-sky-800 mb-3 flex items-center gap-2">
                                    <PieChart className="w-4 h-4" /> Debt Summary
                                </h4>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                                    <div className="p-3 bg-white rounded-lg border border-sky-100">
                                        <div className="text-xs text-slate-500">Revolving Debt</div>
                                        <div className="font-semibold text-slate-800">${block.data.debt_summary.total_revolving_debt?.toLocaleString()}</div>
                                        <div className="text-xs text-slate-500">Limit: ${block.data.debt_summary.total_revolving_limit?.toLocaleString()}</div>
                                    </div>
                                    <div className="p-3 bg-white rounded-lg border border-sky-100">
                                        <div className="text-xs text-slate-500">Utilization</div>
                                        <div className={`font-semibold ${block.data.debt_summary.revolving_utilization_pct > 30 ? 'text-amber-600' : 'text-emerald-600'}`}>
                                            {block.data.debt_summary.revolving_utilization_pct}%
                                        </div>
                                    </div>
                                    <div className="p-3 bg-white rounded-lg border border-sky-100">
                                        <div className="text-xs text-slate-500">Installment Debt</div>
                                        <div className="font-semibold text-slate-800">${block.data.debt_summary.total_installment_debt?.toLocaleString()}</div>
                                    </div>
                                    <div className="p-3 bg-white rounded-lg border border-sky-100">
                                        <div className="text-xs text-slate-500">Mortgage Debt</div>
                                        <div className="font-semibold text-slate-800">${block.data.debt_summary.total_mortgage_debt?.toLocaleString()}</div>
                                    </div>
                                    <div className="p-3 bg-sky-100 rounded-lg border-2 border-sky-300 col-span-2 md:col-span-2">
                                        <div className="text-xs text-sky-600 font-semibold">Total Monthly Payments</div>
                                        <div className="font-bold text-sky-800 text-xl">${block.data.debt_summary.total_monthly_payments?.toLocaleString()}/mo</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Inquiries */}
                        {block.data.inquiries && (
                            <div className="bg-slate-50 rounded-lg border border-slate-200 p-4">
                                <h4 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
                                    <Search className="w-4 h-4" /> Credit Inquiries (Last 12 Months)
                                </h4>
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
                                    <div className="text-center p-2 bg-white rounded border">
                                        <div className="text-xl font-bold text-slate-700">{block.data.inquiries.total_12_months}</div>
                                        <div className="text-xs text-slate-500">Total</div>
                                    </div>
                                    <div className="text-center p-2 bg-white rounded border">
                                        <div className="text-xl font-bold text-sky-600">{block.data.inquiries.mortgage_inquiries}</div>
                                        <div className="text-xs text-slate-500">Mortgage</div>
                                    </div>
                                    <div className="text-center p-2 bg-white rounded border">
                                        <div className="text-xl font-bold text-slate-600">{block.data.inquiries.auto_inquiries}</div>
                                        <div className="text-xs text-slate-500">Auto</div>
                                    </div>
                                    <div className="text-center p-2 bg-white rounded border">
                                        <div className="text-xl font-bold text-slate-600">{block.data.inquiries.credit_card_inquiries}</div>
                                        <div className="text-xs text-slate-500">Credit Card</div>
                                    </div>
                                    <div className="text-center p-2 bg-white rounded border">
                                        <div className="text-xl font-bold text-slate-600">{block.data.inquiries.other_inquiries}</div>
                                        <div className="text-xs text-slate-500">Other</div>
                                    </div>
                                </div>
                                {block.data.inquiries.inquiry_concerns && (
                                    <div className="mt-2 text-xs text-slate-600 italic">{block.data.inquiries.inquiry_concerns}</div>
                                )}
                            </div>
                        )}

                        {/* URLA vs Credit Discrepancies */}
                        {block.data.urla_vs_credit_discrepancies && block.data.urla_vs_credit_discrepancies.length > 0 && (
                            <div className="bg-red-50/50 rounded-lg border border-red-200 p-4">
                                <h4 className="text-sm font-semibold text-red-800 mb-3 flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4" /> URLA vs Credit Report Discrepancies
                                </h4>
                                <div className="space-y-2">
                                    {block.data.urla_vs_credit_discrepancies.map((item, i) => (
                                        <div key={i} className="flex items-start gap-2 text-sm text-red-700">
                                            <AlertOctagon className="w-4 h-4 shrink-0 mt-0.5" />
                                            <span>{item}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                );

            case 'substructure':
                // Generic substructure renderer for audit sections
                const subData = block.data;
                if (!subData) return null;

                // Render critical findings for audit section
                if (subData.critical_findings && Array.isArray(subData.critical_findings)) {
                    return (
                        <div key={idx} className="mb-4 bg-red-50/50 rounded-lg border border-red-100 p-4 pdf-avoid-break">
                            <div className="flex items-center gap-2 mb-3 text-red-800 font-semibold text-sm">
                                <AlertOctagon className="w-4 h-4 text-red-600" />
                                Critical Findings
                            </div>
                            <div className="space-y-2">
                                {subData.critical_findings.map((item, i) => (
                                    <div key={i} className="flex items-start gap-2 text-sm text-red-700 bg-white p-2 rounded border border-red-100">
                                        <span className="font-mono text-xs bg-red-100 px-1.5 py-0.5 rounded">{item.split(':')[0]}</span>
                                        <span>{item.split(':').slice(1).join(':')}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                }

                // Render DTI stress test
                if (subData.dti_stress_test) {
                    return (
                        <div key={idx} className="mb-4 bg-amber-50/50 rounded-lg border border-amber-100 p-4 pdf-avoid-break">
                            <div className="flex items-center gap-2 mb-3 text-amber-800 font-semibold text-sm">
                                <BarChart className="w-4 h-4 text-amber-600" />
                                DTI Stress Test
                            </div>
                            <p className="text-sm text-slate-700">{subData.dti_stress_test}</p>
                        </div>
                    );
                }

                // Render conditions precedent
                if (subData.conditions_precedent && Array.isArray(subData.conditions_precedent)) {
                    return (
                        <div key={idx} className="mb-4 bg-sky-50/50 rounded-lg border border-sky-100 p-4 pdf-avoid-break">
                            <div className="flex items-center gap-2 mb-3 text-sky-800 font-semibold text-sm">
                                <ListChecks className="w-4 h-4 text-sky-600" />
                                Conditions Precedent
                            </div>
                            <div className="space-y-2">
                                {subData.conditions_precedent.map((item, i) => (
                                    <div key={i} className="flex items-start gap-2 text-sm text-slate-700">
                                        <span className="font-mono text-xs bg-sky-100 px-1.5 py-0.5 rounded">{item.split(':')[0]}</span>
                                        <span>{item.split(':').slice(1).join(':')}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                }

                return null;

            case 'markdown':
                // Simple markdown renderer - just show the content as preformatted
                return (
                    <div key={idx} className="mb-4 bg-slate-50 rounded-lg border border-slate-200 p-4 pdf-avoid-break">
                        <div className="flex items-center gap-2 mb-3 text-slate-700 font-semibold text-sm">
                            <FileText className="w-4 h-4 text-slate-500" />
                            Detailed Analysis
                        </div>
                        <div className="prose prose-sm prose-slate max-w-none text-xs whitespace-pre-wrap font-mono bg-white p-3 rounded border border-slate-100 overflow-x-auto">
                            {block.content}
                        </div>
                    </div>
                );

            default: return null;
        }
    };

    if (!isOpen) return null;

    const visibleSections = getVisibleSections();

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-slate-900/80 backdrop-blur-sm" onClick={onClose}>
            <div
                className="relative w-full max-w-7xl h-[90vh] bg-slate-50 rounded-2xl shadow-2xl flex overflow-hidden border border-slate-700"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Sidebar Navigation */}
                <div className="hidden lg:flex flex-col w-72 bg-white border-r border-slate-200 shrink-0 relative z-20">
                    {/* Report Selector */}
                    <div className="p-4 border-b border-slate-100 space-y-2 relative z-10">
                        <div className="text-xs font-bold text-slate-400 px-2 uppercase tracking-wider mb-2">Reports</div>

                        <button
                            type="button"
                            onClick={() => setActiveReportTab('underwriting')}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer ${activeReportTab === 'underwriting' ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50'}`}
                        >
                            <FileText className="w-4 h-4 text-sky-600" /> Underwriting
                        </button>

                        <button
                            type="button"
                            onClick={() => setActiveReportTab('audit')}
                            className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer ${activeReportTab === 'audit' ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50'}`}
                        >
                            <div className="flex items-center gap-3">
                                <FileSearch className="w-4 h-4 text-indigo-600" /> Audit
                            </div>
                            <div className="flex items-center gap-1.5 px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-[10px] font-bold text-slate-500">
                                PRO
                            </div>
                        </button>

                        <button
                            type="button"
                            onClick={() => setActiveReportTab('forensic')}
                            className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer ${activeReportTab === 'forensic' ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50'}`}
                        >
                            <div className="flex items-center gap-3">
                                <Search className="w-4 h-4 text-emerald-600" /> Forensic
                            </div>
                            <div className="flex items-center gap-1.5 px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-[10px] font-bold text-slate-500">
                                PRO
                            </div>
                        </button>
                    </div>

                    <div className="p-6 pb-2 border-b border-slate-100">
                        <p className="text-xs font-bold text-slate-400 px-2 uppercase tracking-wider">Sections</p>
                    </div>

                    <div className="flex-1 overflow-y-auto py-2 relative z-10">
                        {!loading && visibleSections.map((section, idx) => (
                            <button
                                type="button"
                                key={idx}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    scrollToSection(idx);
                                }}
                                className={`w-full text-left px-6 py-2.5 text-sm font-medium flex items-center gap-3 transition-colors cursor-pointer select-none
                                    ${activeSection === idx ? 'bg-sky-50 text-sky-700 border-r-4 border-sky-600' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}
                                `}
                            >
                                <span className={`pointer-events-none ${activeSection === idx ? 'text-sky-600' : 'text-slate-400'}`}>
                                    {getSectionIcon(section.title)}
                                </span>
                                <span className="truncate pointer-events-none">{section.title}</span>
                            </button>
                        ))}
                        {loading && (
                            <div className="p-6 space-y-4">
                                {[1, 2, 3, 4].map(i => <div key={i} className="h-8 bg-slate-100 rounded animate-pulse"></div>)}
                            </div>
                        )}
                        {!loading && visibleSections.length === 0 && (
                            <div className="p-6 text-center text-sm text-slate-400 italic">
                                No sections available for this report.
                            </div>
                        )}
                    </div>
                    <div className="p-4 border-t border-slate-100 bg-slate-50 relative z-10">
                        <button
                            type="button"
                            onClick={handleDownload}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-slate-800 text-white rounded-lg text-sm font-semibold hover:bg-slate-700 transition cursor-pointer"
                            disabled={loading}
                        >
                            <Download className="w-4 h-4" /> Download PDF
                        </button>
                    </div>
                </div>

                {/* Main Content Area */}
                <div className="flex-1 flex flex-col h-full overflow-hidden bg-slate-50/50">

                    {loading ? (
                        <div className="flex-1 flex flex-col items-center justify-center p-8 text-slate-400">
                            <div className="w-8 h-8 border-4 border-slate-200 border-t-sky-600 rounded-full animate-spin mb-4"></div>
                            <p>Loading report data...</p>
                        </div>
                    ) : error ? (
                        <div className="flex-1 flex flex-col items-center justify-center p-8 text-red-500">
                            <AlertOctagon className="w-12 h-12 mb-4 opacity-50" />
                            <p className="font-semibold">Error Loading Report</p>
                            <p className="text-sm opacity-75">{error}</p>
                        </div>
                    ) : (
                        <>
                            {/* Mobile Header */}
                            <div className="lg:hidden flex items-center justify-between px-4 py-3 bg-white border-b border-slate-200">
                                <div className="font-bold text-slate-800 flex items-center gap-2">
                                    <FileText className="w-5 h-5 text-sky-600" />
                                    Report
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2 text-slate-600 bg-slate-100 rounded-lg">
                                        <Menu className="w-5 h-5" />
                                    </button>
                                    <button onClick={onClose} className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg">
                                        <X className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>

                            {/* Mobile Menu Overlay */}
                            {isMobileMenuOpen && (
                                <div className="absolute inset-x-0 top-[57px] bottom-0 z-50 bg-white p-4 overflow-y-auto lg:hidden">
                                    {visibleSections.map((section, idx) => (
                                        <button
                                            type="button"
                                            key={idx}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                scrollToSection(idx);
                                            }}
                                            className="w-full text-left px-4 py-3 border-b border-slate-100 text-sm font-medium text-slate-700 flex items-center gap-3 cursor-pointer"
                                        >
                                            <span className="pointer-events-none">{getSectionIcon(section.title)}</span>
                                            <span className="pointer-events-none">{section.title}</span>
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Desktop Header */}
                            <div className="hidden lg:flex items-center justify-between px-8 py-4 bg-white border-b border-slate-200 shrink-0">
                                <div>
                                    <h2 className="text-xl font-bold text-slate-900">{fileName}</h2>
                                    <p className="text-sm text-slate-500 flex items-center gap-2">
                                        <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                                        AI Analysis Complete • {new Date().toLocaleDateString()}
                                    </p>
                                </div>
                                <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition">
                                    <X className="w-6 h-6" />
                                </button>
                            </div>

                            {/* Scrollable Report Content */}
                            <div ref={containerRef} className="flex-1 overflow-y-auto p-4 sm:p-8 scroll-smooth">
                                <div ref={reportRef} className="max-w-4xl mx-auto space-y-8 pb-20">

                                    {/* Report Title Page */}
                                    <div className="text-center mb-12 border-b pb-8 border-slate-200">
                                        <div className="inline-block mb-4">
                                            {compositeLogo ? (
                                                <img src={compositeLogo} alt="CloudMortgage.io" style={{ height: '56px', width: 'auto', display: 'block' }} />
                                            ) : (
                                                <div className="inline-flex p-4 bg-slate-900 rounded-2xl shadow-lg">
                                                    <img src={LogoBase64} alt="CloudMortgage.io" style={{ height: '40px', width: 'auto', display: 'block' }} />
                                                </div>
                                            )}
                                        </div>
                                        <h1 className="text-3xl font-extrabold text-slate-900 mb-2">
                                            {activeReportTab === 'audit' ? 'Compliance Audit Report' :
                                                activeReportTab === 'forensic' ? 'Forensic Accounting Report' :
                                                    'Underwriting Analysis Report'}
                                        </h1>
                                        <p className="text-slate-500">Case ID: 2024-KCC-INV • Generated via CloudMortgage AI</p>
                                    </div>

                                    {/* Section Cards, Audit Report, or Premium Placeholder */}
                                    {activeReportTab === 'underwriting' ? (
                                        visibleSections.map((section, idx) => (
                                            <section
                                                id={`report-section-${idx}`}
                                                key={idx}
                                                style={{ animationDelay: `${idx * 150}ms`, animationFillMode: 'both' }}
                                                className={`
                                            bg-white rounded-xl shadow-sm border p-6 sm:p-8 scroll-mt-6 transition-all duration-300 pdf-avoid-break animate-slide-up
                                            ${activeSection === idx ? 'ring-2 ring-sky-500/20 shadow-md transform scale-[1.01]' : 'border-slate-200'}
                                        `}
                                            >
                                                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
                                                    <div className={`p-2.5 rounded-lg ${section.isRisk ? 'bg-amber-50 text-amber-600' : 'bg-sky-50 text-sky-600'}`}>
                                                        {getSectionIcon(section.title)}
                                                    </div>
                                                    <div>
                                                        <h2 className="text-xl font-bold text-slate-800">{section.title}</h2>
                                                        {section.isRisk && (
                                                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full mt-1">
                                                                <AlertTriangle className="w-3 h-3" /> Attention Needed
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="prose prose-slate prose-sm max-w-none">
                                                    {section.blocks.map((block, bIdx) => renderBlock(block, bIdx))}
                                                </div>
                                            </section>
                                        ))
                                    ) : activeReportTab === 'audit' ? (
                                        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
                                            {/* Audit Header */}
                                            <div className="bg-white text-slate-900 pb-6 mb-8 border-b-4 border-slate-900">
                                                <div className="flex justify-between items-center">
                                                    <div className="flex items-center gap-4">
                                                        <Siren className="w-12 h-12 text-red-600" />
                                                        <div>
                                                            <h1 className="text-3xl font-extrabold text-slate-900">Underwriting Audit</h1>
                                                            <p className="text-sm text-slate-500">Internal Forensic Analysis • {report2_Audit.metadata.institution}</p>
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="text-4xl font-bold text-red-600">{report2_Audit.metadata.risk}</div>
                                                        <div className="text-xs uppercase tracking-wide text-slate-500">Risk Rating</div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="space-y-10">
                                                {/* Executive Summary */}
                                                <div id="report-section-0" className="scroll-mt-6">
                                                    <h3 className="text-lg font-bold text-slate-800 uppercase mb-4 flex items-center gap-3 border-b-2 border-slate-200 pb-3">
                                                        <ClipboardList className="w-5 h-5 text-slate-500" /> Executive Summary
                                                    </h3>
                                                    <div className="bg-slate-50 p-6 rounded-lg border-l-4 border-red-600 shadow-sm">
                                                        <p className="text-base text-slate-700 italic mb-4">"{report2_Audit.executive_summary.assessment}"</p>
                                                        <div className="inline-block bg-red-600 text-white text-xs font-semibold px-3 py-1.5 rounded-md">
                                                            Recommendation: {report2_Audit.metadata.status}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Critical Findings */}
                                                <div id="report-section-1" className="scroll-mt-6">
                                                    <h3 className="text-lg font-bold text-slate-800 uppercase mb-4 flex items-center gap-3 border-b-2 border-slate-200 pb-3">
                                                        <AlertTriangle className="w-5 h-5 text-red-600" /> Critical Findings
                                                    </h3>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                                                        {report2_Audit.critical_findings.map((f, i) => (
                                                            <div key={i} className="border border-slate-200 rounded-lg p-4 shadow-sm flex gap-4 items-start">
                                                                <div className="shrink-0 pt-1">
                                                                    <span className={`text-xs font-semibold px-2 py-1 rounded-full uppercase ${f.severity === 'CRITICAL' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>
                                                                        {f.severity}
                                                                    </span>
                                                                </div>
                                                                <div className="space-y-1">
                                                                    <h4 className="text-base font-semibold text-slate-800 break-words">{f.id}: {f.title}</h4>
                                                                    <p className="text-sm text-slate-700">{f.desc}</p>
                                                                    <p className="text-xs text-slate-500 italic">Impact: {f.impact}</p>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                                {/* Data Quality & Compliance */}
                                                <div id="report-section-2" className="grid grid-cols-1 md:grid-cols-2 gap-8 scroll-mt-6">
                                                    <div>
                                                        <h3 className="text-lg font-bold text-slate-800 uppercase mb-4 flex items-center gap-3 border-b-2 border-slate-200 pb-3">
                                                            <FileSearch className="w-5 h-5 text-slate-500" /> Data Quality Gaps
                                                        </h3>
                                                        <ul className="space-y-3">
                                                            {report2_Audit.data_quality.map((d, idx) => (
                                                                <li key={idx} className="text-sm text-slate-700">
                                                                    <span className="font-semibold text-slate-800 block">{d.id}: {d.title}</span>
                                                                    {d.details}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                    <div>
                                                        <h3 className="text-lg font-bold text-slate-800 uppercase mb-4 flex items-center gap-3 border-b-2 border-slate-200 pb-3">
                                                            <Scale className="w-5 h-5 text-slate-500" /> Compliance Issues
                                                        </h3>
                                                        <ul className="space-y-3">
                                                            {report2_Audit.compliance_issues.map((c, idx) => (
                                                                <li key={idx} className="text-sm text-slate-700">
                                                                    <span className="font-semibold text-slate-800 block">{c.id}: {c.title}</span>
                                                                    {c.desc}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </div>

                                                {/* DTI Stress Test */}
                                                <div id="report-section-3" className="scroll-mt-6">
                                                    <h3 className="text-lg font-bold text-slate-800 uppercase mb-4 flex items-center gap-3 border-b-2 border-slate-200 pb-3">
                                                        <BarChart className="w-5 h-5 text-slate-500" /> DTI Stress Test
                                                    </h3>
                                                    <div className="grid grid-cols-2 gap-6 text-center">
                                                        <div className="bg-white border-2 border-amber-300 rounded-lg p-6 shadow-md">
                                                            <div className="text-xs font-semibold text-slate-500 uppercase mb-2">{report2_Audit.dti_analysis.optimistic.name}</div>
                                                            <div className="text-4xl font-extrabold text-amber-500">{report2_Audit.dti_analysis.optimistic.dti}%</div>
                                                            <div className="text-xs text-slate-600 mt-2">Income: ${report2_Audit.dti_analysis.optimistic.income.toLocaleString()}</div>
                                                        </div>
                                                        <div className="bg-white border-2 border-red-300 rounded-lg p-6 shadow-md">
                                                            <div className="text-xs font-semibold text-red-500 uppercase mb-2">{report2_Audit.dti_analysis.conservative.name}</div>
                                                            <div className="text-4xl font-extrabold text-red-700">{report2_Audit.dti_analysis.conservative.dti}%</div>
                                                            <div className="text-xs text-red-600 mt-2">Income: ${report2_Audit.dti_analysis.conservative.income.toLocaleString()}</div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Conditions Precedent */}
                                                <div id="report-section-4" className="scroll-mt-6">
                                                    <h3 className="text-lg font-bold text-slate-800 uppercase mb-4 flex items-center gap-3 border-b-2 border-slate-200 pb-3">
                                                        <Check className="w-5 h-5 text-slate-500" /> Conditions Precedent
                                                    </h3>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                        <div className="bg-red-50 border-l-4 border-red-500 p-5 rounded-r-lg">
                                                            <h4 className="text-sm font-semibold text-red-800 uppercase mb-3">Tier 1: Critical (Before Approval)</h4>
                                                            <ul className="list-decimal list-inside space-y-2 text-sm text-slate-700">
                                                                {report2_Audit.conditions_precedent.tier1.map((c, idx) => (
                                                                    <li key={idx} className="text-sm text-slate-700">{c}</li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                        <div className="bg-amber-50 border-l-4 border-amber-500 p-5 rounded-r-lg">
                                                            <h4 className="text-sm font-semibold text-amber-800 uppercase mb-3">Tier 2: Required for Closing</h4>
                                                            <ul className="list-decimal list-inside space-y-2 text-sm text-slate-700">
                                                                {report2_Audit.conditions_precedent.tier2.map((c, idx) => (
                                                                    <li key={idx} className="text-sm text-slate-700">{c}</li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Final Rec & Cert */}
                                                <div id="report-section-5" className="bg-slate-800 text-white p-6 rounded-lg mt-10 scroll-mt-6">
                                                    <div className="font-semibold text-base uppercase mb-3 text-sky-300">Final Recommendation: {report2_Audit.final_recommendation.decision}</div>
                                                    <p className="mb-3 text-sm text-slate-300">{report2_Audit.final_recommendation.rationale}</p>
                                                    <div className="border-t border-slate-600 pt-3 mt-4 text-xs text-slate-500 italic">                    Certification: {report2_Audit.certification}
                                                    </div>
                                                </div>

                                            </div>
                                        </div>
                                    ) : (
                                        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
                                            <div className="bg-white text-slate-900 pb-6 mb-8 border-b-4 border-slate-900">
                                                <div className="flex justify-between items-center">
                                                    <div className="flex items-center gap-4">
                                                        <FileSearch className="w-12 h-12 text-sky-600" />
                                                        <div>
                                                            <h1 className="text-3xl font-extrabold text-slate-900">Forensic Financial Report</h1>
                                                            <p className="text-sm text-slate-500">
                                                                Borrower: {report3_Forensic.applicants?.[0]?.name || "Borrower"} • Source: Gemini Forensic Extract
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="text-sm font-semibold text-slate-600">Risk Flags</div>
                                                        <div className="text-xs text-slate-500">Liquidity • Commingling • Trading Volatility</div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="space-y-10">
                                                {/* Applicants & Business */}
                                                <div id="report-section-0" className="grid grid-cols-1 lg:grid-cols-2 gap-6 scroll-mt-6">
                                                    <div className="border border-slate-200 rounded-xl p-4 shadow-sm">
                                                        <h3 className="text-lg font-bold text-slate-800 uppercase mb-3 flex items-center gap-2">
                                                            <ClipboardList className="w-5 h-5 text-sky-600" /> Applicants
                                                        </h3>
                                                        <div className="space-y-3 text-sm text-slate-700">
                                                            {report3_Forensic.applicants.map((a, idx) => (
                                                                <div key={idx} className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                                                                    <p className="font-semibold text-slate-900">{a.name}</p>
                                                                    <p className="text-slate-600">{a.role}</p>
                                                                    <p className="text-slate-500 text-xs">Address: {a.current_address}</p>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    <div className="border border-slate-200 rounded-xl p-4 shadow-sm">
                                                        <h3 className="text-lg font-bold text-slate-800 uppercase mb-3 flex items-center gap-2">
                                                            <BarChart className="w-5 h-5 text-sky-600" /> Business Overview
                                                        </h3>
                                                        {report3_Forensic.businesses.map((biz, idx) => (
                                                            <div key={idx} className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-700 space-y-2">
                                                                <div className="flex justify-between">
                                                                    <span className="font-semibold text-slate-800">{biz.name}</span>
                                                                    <span className="text-xs bg-sky-100 text-sky-700 px-2 py-0.5 rounded-full">EIN: {biz.ein}</span>
                                                                </div>
                                                                <p className="text-slate-600">{biz.type}</p>
                                                                <div className="text-xs text-slate-500 space-y-1">
                                                                    <p>State: {biz.formation?.state || "N/A"}</p>
                                                                    <p>File #: {biz.formation?.file_number || "N/A"}</p>
                                                                    <p>Formed: {biz.formation?.formation_date || "N/A"}</p>
                                                                </div>
                                                                <div className="text-xs text-slate-600">
                                                                    Ownership: {biz.formation?.ownership?.map((o) => `${o.partner} (${o.percentage}%)`).join(", ")}
                                                                </div>
                                                                <div className="text-xs text-slate-600">
                                                                    2024 Net (1065): {(biz.tax_return_summaries?.[0]?.summary?.net_income_loss_total ?? biz.tax_return_2024?.form_1065_summary?.ordinary_business_income_loss ?? 0).toLocaleString()}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                                {/* Banking */}
                                                <div id="report-section-1" className="grid grid-cols-1 lg:grid-cols-2 gap-6 scroll-mt-6">
                                                    <div className="border border-slate-200 rounded-xl p-4 shadow-sm">
                                                        <h3 className="text-lg font-bold text-slate-800 uppercase mb-3 flex items-center gap-2">
                                                            <FileSearch className="w-5 h-5 text-sky-600" /> Business Banking
                                                        </h3>
                                                        {report3_Forensic.bank.business_accounts?.map((acct, idx) => (
                                                            <div key={idx} className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-700 space-y-2">
                                                                <div className="flex justify-between">
                                                                    <span className="font-semibold text-slate-800">{acct.institution}</span>
                                                                    <span className="text-xs text-slate-500">{acct.account_type} • •{acct.account_last4 || "----"}</span>
                                                                </div>
                                                                {acct.annual_summaries?.[0] && (
                                                                    <div className="text-xs text-slate-600 space-y-1">
                                                                        <p>Total Deposits: ${acct.annual_summaries[0].total_deposits.toLocaleString()}</p>
                                                                        <p>Total Withdrawals: ${acct.annual_summaries[0].total_withdrawals.toLocaleString()}</p>
                                                                        <p>Net Cash Flow: ${acct.annual_summaries[0].net_cash_flow.toLocaleString()}</p>
                                                                        <p>Ending Balance: ${acct.annual_summaries[0].ending_balance?.toLocaleString?.() || "-"}</p>
                                                                    </div>
                                                                )}
                                                                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">{acct.notes}</p>
                                                            </div>
                                                        ))}
                                                    </div>

                                                    <div className="border border-slate-200 rounded-xl p-4 shadow-sm">
                                                        <h3 className="text-lg font-bold text-slate-800 uppercase mb-3 flex items-center gap-2">
                                                            <Cloud className="w-5 h-5 text-sky-600" /> Personal Banking
                                                        </h3>
                                                        <div className="space-y-3">
                                                            {report3_Forensic.bank.personal_accounts?.map((acct, idx) => (
                                                                <div key={idx} className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-700 space-y-2">
                                                                    <div className="flex justify-between">
                                                                        <span className="font-semibold text-slate-800">{acct.institution}</span>
                                                                        <span className="text-xs text-slate-500">{acct.account_type} • •{acct.account_last4 || "----"}</span>
                                                                    </div>
                                                                    {acct.monthly_snapshots?.map((snap, snapIdx) => (
                                                                        <div key={snapIdx} className="text-xs text-slate-600 flex justify-between">
                                                                            <span>{snap.period}</span>
                                                                            <span className="text-right">
                                                                                +${snap.deposits.toLocaleString()} / -${snap.withdrawals.toLocaleString()} (End: ${snap.ending_balance.toLocaleString()})
                                                                            </span>
                                                                        </div>
                                                                    ))}
                                                                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">{acct.notes}</p>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Investments & Income */}
                                                <div id="report-section-2" className="grid grid-cols-1 lg:grid-cols-2 gap-6 scroll-mt-6">
                                                    <div className="border border-slate-200 rounded-xl p-4 shadow-sm">
                                                        <h3 className="text-lg font-bold text-slate-800 uppercase mb-3 flex items-center gap-2">
                                                            <BarChart className="w-5 h-5 text-sky-600" /> Investment Accounts
                                                        </h3>
                                                        <div className="space-y-3">
                                                            {report3_Forensic.investments.map((inv, idx) => (
                                                                <div key={idx} className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-700 space-y-2">
                                                                    <div className="flex justify-between">
                                                                        <span className="font-semibold text-slate-800">{inv.account}</span>
                                                                        <span className="text-xs text-slate-500">{inv.owner}</span>
                                                                    </div>
                                                                    <div className="text-xs text-slate-600 space-y-1">
                                                                        {inv.value_history?.map((vh, vidx) => (
                                                                            <div key={vidx} className="flex justify-between">
                                                                                <span>{vh.as_of}</span>
                                                                                <span>${vh.net_value.toLocaleString()}</span>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">{inv.notes}</p>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    <div className="border border-slate-200 rounded-xl p-4 shadow-sm">
                                                        <h3 className="text-lg font-bold text-slate-800 uppercase mb-3 flex items-center gap-2">
                                                            <Scale className="w-5 h-5 text-sky-600" /> Income & Liabilities
                                                        </h3>
                                                        <div className="space-y-3 text-sm text-slate-700">
                                                            <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-1">
                                                                <p className="font-semibold text-slate-800">Qualifying Income</p>
                                                                <p className="text-xs text-slate-600">
                                                                    W-2 Gross Monthly: $
                                                                    {(() => {
                                                                        const monthlyFromQual =
                                                                            report3_Forensic.income?.qualifying_income_summary?.base_employment_income_gross_monthly;
                                                                        const annualW2 = report3_Forensic.income?.personal_tax_return_summaries?.[0]?.summary?.w2_wages;
                                                                        const monthlyFromW2 = annualW2 ? annualW2 / 12 : null;
                                                                        const val = monthlyFromQual || monthlyFromW2 || 0;
                                                                        return Number(val).toLocaleString();
                                                                    })()}
                                                                </p>
                                                                <p className="text-xs text-slate-500">
                                                                    {report3_Forensic.income?.qualifying_income_summary?.notes ||
                                                                        "Qualifying income sourced from W-2; rental cash flow reflected in liabilities."}
                                                                </p>
                                                            </div>
                                                            <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-1">
                                                                <p className="font-semibold text-slate-800">Tax Snapshot (1040)</p>
                                                                <p className="text-xs text-slate-600">
                                                                    AGI: $
                                                                    {Number(
                                                                        report3_Forensic.income?.personal_tax_return_summaries?.[0]?.summary?.agi || 0
                                                                    ).toLocaleString()}
                                                                </p>
                                                                <p className="text-xs text-slate-600">
                                                                    Capital Gains (Net): $
                                                                    {Number(
                                                                        report3_Forensic.income?.personal_tax_return_summaries?.[0]?.summary?.capital_gains_net || 0
                                                                    ).toLocaleString()}
                                                                </p>
                                                                <p className="text-xs text-slate-600">
                                                                    Net Investment Income Tax: $
                                                                    {Number(
                                                                        report3_Forensic.income?.personal_tax_return_summaries?.[0]?.summary?.other_items
                                                                            ?.net_investment_income_tax || 0
                                                                    ).toLocaleString()}
                                                                </p>
                                                                <p className="text-xs text-slate-600">
                                                                    Additional Medicare Tax: $
                                                                    {Number(
                                                                        report3_Forensic.income?.personal_tax_return_summaries?.[0]?.summary?.other_items
                                                                            ?.additional_medicare_tax || 0
                                                                    ).toLocaleString()}
                                                                </p>
                                                                <p className="text-xs text-slate-600">
                                                                    HSA Excess Contribution Penalty:
                                                                    {" "}
                                                                    {report3_Forensic.income?.personal_tax_return_summaries?.[0]?.summary?.other_items
                                                                        ?.hsa_excess_contrib_penalty_flag
                                                                        ? "Yes"
                                                                        : "No"}
                                                                </p>
                                                            </div>
                                                            <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-1">
                                                                <p className="font-semibold text-slate-800">Liabilities</p>
                                                                <p className="text-xs text-slate-600">
                                                                    Total Estimated Monthly Mortgage Debt: $
                                                                    {Number(
                                                                        report3_Forensic.liabilities?.total_estimated_monthly_mortgage_debt || 0
                                                                    ).toLocaleString()}
                                                                </p>
                                                                {report3_Forensic.liabilities?.regions_bank_estimated_payment !== undefined && (
                                                                    <p className="text-xs text-slate-600">
                                                                        Regions Bank Estimated: $
                                                                        {Number(report3_Forensic.liabilities?.regions_bank_estimated_payment || 0).toLocaleString()}
                                                                    </p>
                                                                )}
                                                                <p className="text-xs text-slate-600">
                                                                    {report3_Forensic.liabilities?.other_debts ||
                                                                        report3_Forensic.liabilities?.mortgage_obligations ||
                                                                        report3_Forensic.liabilities?.notes ||
                                                                        "Liability details pending credit report."}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Risks & Follow-ups */}
                                                <div id="report-section-3" className="grid grid-cols-1 lg:grid-cols-2 gap-6 scroll-mt-6">
                                                    <div className="border border-slate-200 rounded-xl p-4 shadow-sm">
                                                        <h3 className="text-lg font-bold text-slate-800 uppercase mb-3 flex items-center gap-2">
                                                            <AlertTriangle className="w-5 h-5 text-red-600" /> Forensic Risks
                                                        </h3>
                                                        <ul className="space-y-3 text-sm text-slate-700">
                                                            {report3_Forensic.risks.map((risk, idx) => (
                                                                <li key={idx} className="p-3 rounded-lg bg-red-50 border border-red-100">
                                                                    <div className="flex justify-between items-center">
                                                                        <span className="font-semibold text-slate-800">
                                                                            {risk.risk_id} — {risk.category}
                                                                        </span>
                                                                        <span className="text-xs font-bold text-red-600 uppercase">{risk.severity}</span>
                                                                    </div>
                                                                    <p className="text-xs text-slate-700 mt-1">{risk.description}</p>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                    <div className="border border-slate-200 rounded-xl p-4 shadow-sm">
                                                        <h3 className="text-lg font-bold text-slate-800 uppercase mb-3 flex items-center gap-2">
                                                            <ClipboardList className="w-5 h-5 text-sky-600" /> Required Follow-ups
                                                        </h3>
                                                        <ul className="space-y-2 text-sm text-slate-700 list-disc list-inside">
                                                            {report3_Forensic.followUps.map((item, idx) => (
                                                                <li key={idx}>{item}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </div>

                                                {/* Documents Referenced */}
                                                {report3_Forensic.documents?.length ? (
                                                    <div id="report-section-4" className="border border-slate-200 rounded-xl p-4 shadow-sm scroll-mt-6">
                                                        <h3 className="text-lg font-bold text-slate-800 uppercase mb-3 flex items-center gap-2">
                                                            <FileText className="w-5 h-5 text-sky-600" /> Documents Referenced
                                                        </h3>
                                                        <p className="text-sm text-slate-700">{report3_Forensic.documents.join(", ")}</p>
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>
                                    )}

                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div >
        </div >
    );
};

DetailedUnderwritingReport.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    autoDownload: PropTypes.bool,
    fileName: PropTypes.string,
    reportUrl: PropTypes.string,
};

export default DetailedUnderwritingReport;
